import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import Header from "../../components/Header";
import axios from "axios";
import { useTranslation } from "react-i18next";
import { getApiEndpoint } from "../../utils/apiConfig";

const Signup = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  document.title = t("signup.registration") || "Qeydiyyat";
  const headerTitle = t("signup.headerTitle") || "Şəxsi kabinetə giriş";
  
  const [formData, setFormData] = useState({
    name: "",
    surname: "",
    email: "",
    password: "",
    confirmPassword: "",
    phoneNumber: ""
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validatePassword = (password) => {
    return password.length >= 8;
  };

  const isFormValid = 
    formData.name.trim() !== "" && 
    formData.surname.trim() !== "" &&
    formData.email.trim() !== "" &&
    validateEmail(formData.email) &&
    formData.password.trim() !== "" &&
    validatePassword(formData.password) &&
    formData.password === formData.confirmPassword;

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!isFormValid) {
      if (!formData.name.trim()) {
        setError(t("signup.validation"));
        return;
      }
      if (!formData.surname.trim()) {
        setError(t("signup.validation"));
        return;
      }
      if (!formData.email.trim()) {
        setError(t("signup.emailRequired"));
        return;
      }
      if (!validateEmail(formData.email)) {
        setError(t("signup.emailInvalid"));
        return;
      }
      if (!formData.password.trim()) {
        setError(t("signup.passwordRequired"));
        return;
      }
      if (!validatePassword(formData.password)) {
        setError(t("signup.passwordMinLength"));
        return;
      }
      if (formData.password !== formData.confirmPassword) {
        setError(t("signup.passwordMismatch"));
        return;
      }
      return;
    }
    
    setLoading(true);
    setError("");
    
    try {
      const userData = {
        name: formData.name.trim(),
        surname: formData.surname.trim(),
        email: formData.email.trim(),
        password: formData.password,
        phoneNumber: formData.phoneNumber.trim()
      };

      const response = await axios.post(getApiEndpoint('auth/register'), userData);
      
      if (response.data && response.data.token) {
        // Store the token and user info
        localStorage.setItem("token", response.data.token);
        localStorage.setItem("authToken", response.data.token);
        localStorage.setItem("user", JSON.stringify(response.data.user));
        
        // Show success message
        setError("");
        
        // Check for redirection
        const redirectAfterLogin = localStorage.getItem('redirectAfterLogin');
        const pendingRepairId = localStorage.getItem('pendingRepairId');
        
        if (redirectAfterLogin) {
          localStorage.removeItem('redirectAfterLogin');
          window.location.replace(redirectAfterLogin);
        } else if (pendingRepairId) {
          localStorage.removeItem('pendingRepairId');
          navigate(`/allmaterials/${pendingRepairId}`);
        } else {
          navigate("/account");
        }
      } else {
        setError(t("signup.error"));
      }
    } catch (error) {
      console.error("Registration error:", error);
      console.error("Error response:", error.response?.data);
      console.error("Error status:", error.response?.status);
      
      if (error.response && error.response.data) {
        // Log the errors array to see what validation failed
        if (error.response.data.errors) {
          console.error("Validation errors:", error.response.data.errors);
        }
        
        if (error.response.data.errors && error.response.data.errors.length > 0) {
          // Validation errors from express-validator
          const firstError = error.response.data.errors[0];
          console.error("First validation error:", firstError);
          setError(firstError.msg || firstError.message || firstError);
        } else if (error.response.data.message) {
          if (error.response.data.message.includes('kullanımda')) {
            setError(t("signup.emailExists"));
          } else {
            setError(error.response.data.message);
          }
        } else {
          setError(t("signup.error"));
        }
      } else {
        setError(t("signup.error"));
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="signup-section mx-3">
      <Header 
        title={headerTitle} 
        showProfileIcon={false}
        showBackIcon={false}
      />
      <h2>{t("signup.0")}</h2>
      
      {error && <div className="alert alert-danger">{error}</div>}
      
      <div className="top-section">
        <form onSubmit={handleSubmit} className="d-flex flex-column">
          <div className="name-section">
            <label className="mb-1">{t("signup.name")}</label>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              placeholder={t("signup.name")}
              required
            />
          </div>

          <div className="surname-section">
            <label className="mb-1">{t("signup.surname")}</label>
            <input
              type="text"
              name="surname"
              value={formData.surname}
              onChange={handleInputChange}
              placeholder={t("signup.surname")}
              required
            />
          </div>
          
          <div className="email-section">
            <label className="mb-1">{t("signup.email")}</label>
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleInputChange}
              placeholder={t("signup.enterEmail")}
              required
            />
          </div>

          <div className="password-section">
            <label className="mb-1">{t("signup.password")}</label>
            <input
              type="password"
              name="password"
              value={formData.password}
              onChange={handleInputChange}
              placeholder={t("signup.enterPassword")}
              required
            />
          </div>

          <div className="confirm-password-section">
            <label className="mb-1">{t("signup.confirmPassword")}</label>
            <input
              type="password"
              name="confirmPassword"
              value={formData.confirmPassword}
              onChange={handleInputChange}
              placeholder={t("signup.enterConfirmPassword")}
              required
            />
          </div>

          <div className="phone-section">
            <label className="mb-1">{t("signup.mobileNumber")}</label>
            <input
              type="tel"
              name="phoneNumber"
              value={formData.phoneNumber}
              onChange={handleInputChange}
              placeholder={t("signup.enterPhoneNumber")}
            />
          </div>


          <div className="button-section">
            <button 
              type="submit" 
              className="btn"
              disabled={!isFormValid || loading}
            >
              {loading ? t("signup.loading") : t("signup.3")}
            </button>
          </div>
        </form>
      </div>
      
      <div className="sign-up-section d-flex align-items-center justify-content-center mt-4">
        <h6>
          {t("signup.7")} &nbsp;
          <Link to="/login" className="signup">
            {t("signup.8")}
          </Link>
        </h6>
      </div>
    </div>
  );
};

export default Signup;